import type { RaidTarget } from '@/systems/RaidManager';
import { RaidManager } from '@/systems/RaidManager';
import { formatNumber } from '@/utils/format';

export class RaidPanel {
  private container: HTMLElement;
  private targetsEl: HTMLElement;
  private statusEl: HTMLElement;
  private raidsEl: HTMLElement;
  private refreshBtn: HTMLButtonElement;
  private onRefresh: () => void;
  private onRaid: (targetId: string, troops: number) => void;

  constructor(
    parent: HTMLElement,
    onRefresh: () => void,
    onRaid: (targetId: string, troops: number) => void,
  ) {
    this.onRefresh = onRefresh;
    this.onRaid = onRaid;

    this.container = document.createElement('div');
    this.container.id = 'raid-panel';
    this.container.className = 'game-panel';
    this.container.style.borderColor = 'rgba(231, 76, 60, 0.5)';

    this.container.innerHTML = `
      <div class="game-panel-header">
        <div class="game-panel-title" style="color: var(--red);">⚔️ RAIDS</div>
        <button class="game-panel-close">&times;</button>
      </div>
      <div id="raid-raids" style="font-size: 11px; color: var(--text-dim); text-align: center; margin-bottom: 12px;"></div>
      <div id="raid-status" style="font-size: 11px; color: var(--green); text-align: center; margin-bottom: 8px;"></div>
      <div id="raid-targets"></div>
    `;

    this.refreshBtn = document.createElement('button');
    this.refreshBtn.className = 'btn';
    this.refreshBtn.textContent = '🔍 FIND TARGETS';
    this.refreshBtn.style.cssText = 'width: 100%; padding: 10px; font-size: 12px; margin-top: 8px; color: var(--red); background: var(--bg-button);';
    this.refreshBtn.addEventListener('click', () => this.onRefresh());
    this.container.appendChild(this.refreshBtn);

    this.targetsEl = this.container.querySelector('#raid-targets')!;
    this.statusEl = this.container.querySelector('#raid-status')!;
    this.raidsEl = this.container.querySelector('#raid-raids')!;
    this.container.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());

    parent.appendChild(this.container);
  }

  updateRaidsRemaining(raidManager: RaidManager): void {
    this.raidsEl.textContent = `${raidManager.getRaidsRemaining()} raids remaining today`;
    this.refreshBtn.disabled = !raidManager.canRaid();
    this.refreshBtn.style.opacity = raidManager.canRaid() ? '1' : '0.4';
  }

  setTargets(targets: RaidTarget[], foodAvailable: number): void {
    this.targetsEl.innerHTML = '';
    if (targets.length === 0) {
      this.targetsEl.innerHTML = '<div style="color:#666; text-align:center; padding:12px;">No targets found</div>';
      return;
    }

    for (const t of targets) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 4px; border-bottom: 1px solid #1a1a2e;
      `;

      const info = document.createElement('div');
      info.innerHTML = `
        <div style="color:#c9a44c">${t.username}</div>
        <div style="font-size: 10px; color: #666;">DEF: ${formatNumber(t.defensePower)} | +${formatNumber(t.productionRate)}/s</div>
      `;

      const btn = document.createElement('button');
      const troopCost = Math.max(10, Math.floor(t.defensePower * 1.2));
      btn.textContent = `RAID (${formatNumber(troopCost)} Fd)`;
      btn.style.cssText = `
        background: #2d1a1a; color: #e74c3c; border: 1px solid #333;
        padding: 6px 10px; font-family: monospace; font-size: 11px;
        border-radius: 4px; cursor: pointer;
        touch-action: manipulation;
      `;
      const canAffordRaid = foodAvailable >= troopCost;
      btn.disabled = !canAffordRaid;
      btn.style.opacity = canAffordRaid ? '1' : '0.4';
      btn.addEventListener('click', () => this.onRaid(t.id, troopCost));

      row.appendChild(info);
      row.appendChild(btn);
      this.targetsEl.appendChild(row);
    }
  }

  setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  show(): void {
    this.container.style.display = 'block';
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  toggle(): void {
    if (this.container.style.display === 'none') {
      this.show();
    } else {
      this.hide();
    }
  }

  destroy(): void {
    this.container.remove();
  }
}
